import { Button, Col, Colors, IcPlus, Row, Text } from 'basic-ui-lib';
import styled from 'styled-components';
import convertNumberToSeconds from 'utils/convertNumberToSeconds';
import { useDraggable } from '@dnd-kit/core';
import { useCallback, useMemo, useRef } from 'react';
import { Form } from 'antd';
import { IContentItem } from 'modules/playlist/interfaces/api';
import { IMediaItem, MediaType } from 'modules/media/interfaces/api';
import getVideoDuration from 'utils/getVideoDuration';
import { isMp4Url } from 'utils/getYoutubeId';
import { LayoutCode } from 'modules/channel/services/constants';
import ContentMobileModal, { ContentMobileModalRef } from './ContentMobileModal';
import DragDropPlaylistMobile from './DragDropPlaylistMobile';

export interface ContentFormValues {
  layoutCode: LayoutCode;
  storeId: string;
  screenName: string;
  contentItems: IContentItem[];
  deviceId: string;
  playListId: string;
}

interface ContentPickerSectionMobileProps {
  type: string;
}
const ContentPickerSectionMobile = ({ type }: ContentPickerSectionMobileProps) => {
  const form = Form.useFormInstance<ContentFormValues>();
  const contentMobileModalRef = useRef<ContentMobileModalRef>(null);
  const contentItems = Form.useWatch('contentItems', form) || [];

  const { setNodeRef } = useDraggable({
    id: 'SCREEN_DROP_ZONE',
    data: { type: { type } },
  });

  const totalDuration = useMemo(() => {
    return contentItems.reduce((sum: number, item: IContentItem) => sum + (item?.duration || 0), 0);
  }, [contentItems]);

  const handleZoneClick = () => {
    const currentContentItems: IContentItem[] = form.getFieldValue('contentItems') || [];
    const currentIds = new Set<string>(currentContentItems.map((item) => item.id));

    contentMobileModalRef.current?.open(currentIds, async ({ added, removed }) => {
      await handleContentDelta(added, removed);
    });
  };

  const handleContentDelta = useCallback(
    async (added: IMediaItem[], removed: IMediaItem[]) => {
      let current: IContentItem[] = form.getFieldValue('contentItems') || [];
      
      current = current.filter((item) => !removed.some((r) => r.id === item.id));

      const addedItems: IContentItem[] = await Promise.all(
        added.map(async (c) => {
          let duration = 60;

          const isVideo = c.type === MediaType.VIDEO || (c.type === MediaType.FILE && isMp4Url(c.url));

          if (isVideo) {
            try {
              duration = await Promise.race([
                getVideoDuration(c.url),
                new Promise<number>((resolve) =>
                  setTimeout(() => resolve(60), 3000)
                ),
              ]);
            } catch {
              duration = 60;
            }
          }

          return {
            url: c.url || '',
            type: c.type,
            duration,
            indexPlay: 0,
            name: c.name,
            id: c.id,
            autoReplay: c.autoReplay ?? true,
          };
        })
      );

      let next = [...current, ...addedItems];

      next = next.map((item, index) => ({
        ...item,
        indexPlay: index,
      }));

      form.setFieldsValue({ contentItems: next });
    },
    [form],
  );
  
  return (
    <>
      <Row padding='0 16px' justifyContent='space-between'>
        <Col gap={0}>
          <Text body_1 regular color='neutral.text.3' left>
            Total Duration:
          </Text>
          <Text title_h7 medium left>
            {convertNumberToSeconds(totalDuration)}
          </Text>
        </Col>
        <Button
          type_new
          md
          prefix={<IcPlus width={20} height={20} fill={Colors.primary[5]} />}
          onClick={() => handleZoneClick()}
          style={{ padding: '12px' }}
        >
          Add Content
        </Button>
      </Row>
      <Content ref={setNodeRef}>
        <DragDropPlaylistMobile />
      </Content>
      <ContentMobileModal ref={contentMobileModalRef} />
    </>
  );
};

export default ContentPickerSectionMobile;

const Content = styled.div`
  padding: 0 16px 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  overflow-y: auto;
`;
